import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BankAccount, ContactInformation, LutInfo, MsmeInfo, SezInfo, TdsInfo } from "../../models/masters/contact-information.interface";
import { Form, FormGroup } from "@angular/forms";


export const CONTACT_ACTIONS = {
    ADD: "add",
    EDIT: "edit",
    VIEW: "view",
    CLONE: "clone"
}
export type ContactActions = typeof CONTACT_ACTIONS[keyof typeof CONTACT_ACTIONS];

@Injectable({
    providedIn: "root"
})
export class ContactService {

    constructor() {
    }

    public createContactFromForm(form: FormGroup): ContactInformation {
        const formValue = form.value;

        const sezInfo = formValue?.sez ? {
            sez: formValue?.sez,
            sezCertificateNo: formValue?.sezCertificateNo ?? null,
            sezValidityDate: formValue?.sezValidityDate ?? null
        } as SezInfo : null;

        const contactInformation: ContactInformation = {
            ...formValue,
            address: this.formatAddress(formValue.addressLine1, formValue.addressLine2),
            country: {
                id: formValue.country?.key,
                name: formValue.country?.value
            },
            defaultContact: formValue.defaultContact ?? false,
            cityId: formValue.cityId?.key,
            stateId: formValue.stateId?.key,
            cityName: formValue.cityId?.value,
            stateName: formValue.stateId?.value,
            sezInfo: sezInfo
        }
        return contactInformation;
    }

    public patchContactForm(form: FormGroup, contactInformation: ContactInformation, action: ContactActions): void {
        if (!contactInformation) return;
        
        const isCloning = action === CONTACT_ACTIONS.CLONE;
        
        const locationData = {
            cityId: this.getLocationOption(contactInformation.cityId, contactInformation.cityName),
            stateId: this.getLocationOption(contactInformation.stateId, contactInformation.stateName),
            country: this.getLocationOption(contactInformation.country?.id, contactInformation.country?.name)
        };

        const sezData = {
            sez: isCloning ? false : contactInformation?.sezInfo?.sez ?? false,
            sezCertificateNo: contactInformation?.sezInfo?.sezCertificateNo ?? null,
            sezValidityDate: contactInformation?.sezInfo?.sezValidityDate ?? null
        };

        const baseData = {
            ...contactInformation,
            id: isCloning ? null : contactInformation.id,
            contactName: isCloning ? contactInformation.contactName + ' - Copy' : contactInformation.contactName,
            defaultContact: isCloning ? false : contactInformation.defaultContact,
            contactPersons: isCloning ? [] : contactInformation.contactPersons
        };

        form.patchValue({
            ...baseData,
            ...locationData,
            ...sezData
        });
    }

    private getLocationOption(id: string | undefined, name: string | undefined): { key: string, value: string } | null {
        return id && name ? { key: id, value: name } : null;
    }

    getFormattedContactSummary(contact: ContactInformation): string {
        const parts: string[] = [];
      
        if (contact.address) parts.push(`Address: ${contact.address}`);
        if (contact.email) parts.push(`Email: ${contact.email}`);
        if (contact.directLine) parts.push(`Direct: ${contact.directLine}`);
        if (contact.boardLine) parts.push(`Board: ${contact.boardLine}`);
        if (contact.gstNumber) parts.push(`GST: ${contact.gstNumber}`);
        if (contact.sezInfo?.sez) parts.push(`SEZ: ${contact.sezInfo?.sez ? 'Yes' : 'No'}`);      
        return parts.join(' | ');
    }

    getFormattedBankSummary(account: BankAccount): string {
        const parts: string[] = [];
      
        if (account.accountName) parts.push(`Account Name: ${account.accountName}`);
        if (account.accountNo) parts.push(`Account No: ${account.accountNo}`);
        if (account?.bankMetadata?.name) parts.push(`Bank: ${account.bankMetadata.name}`);
        if (account?.bankMetadata?.branch) parts.push(`Branch: ${account.bankMetadata.branch}`);
        if (account?.bankMetadata?.ifscCode) parts.push(`IFSC Code: ${account.bankMetadata.ifscCode}`);
        return parts.join(' | ');
    }

    getContactBadges(contact: ContactInformation): { label: string; class: string }[] {
        const badges = [
            contact.active ?  { label: 'Active', class: 'active' } : { label: 'Inactive', class: 'inactive' },
            contact.defaultContact ? { label: 'Default', class: 'default' } : null
        ];
      
        return badges.filter((b): b is { label: string; class: string } => !!b);
    }

    getContactActions(view: boolean): { icon: string; name: string; class: string }[] {
        if (view) {
          return [{ icon: '/assets/icons/view_icon.svg', name: 'view', class: 'view' }]
        } else {
          return [
            { icon: '/assets/icons/pencil_icon.svg', name: 'edit', class: 'edit' },
            { icon: '/assets/icons/clone_icon.svg', name: 'clone', class: 'clone' }
          ]
        }
      }

    getBankAccountBadges(account: BankAccount): { label: string; class: string }[] {
        const badges = [
            account.active ?  { label: 'Active', class: 'active' } : { label: 'Inactive', class: 'inactive' },
        ];
        return badges.filter((b): b is { label: string; class: string } => !!b);
    }

    private formatAddress(line1?: string, line2?: string): string {
        return line2?.trim() ? `${line1}, ${line2}` : line1 || '';
    }

    public buildMsmeInfo(formGroup: FormGroup): MsmeInfo {
        const { msme, udyamNo, msmeType, msmeValidityDate } = formGroup.value;

        return {
            msme,
            udyamNo: udyamNo ?? null,
            msmeType: msmeType ?? null,
            msmeValidityDate: msmeValidityDate ?? null,
        };
    }
    
    public buildLutInfo(formGroup: FormGroup): LutInfo {
        const { lut, lutNo, lutValidityDate } = formGroup.value;
        return {
            lut,
            lutNo: lutNo ?? null,
            lutValidityDate: lutValidityDate ?? null,
        };
    }
    
    public buildTdsInfo(formGroup: FormGroup): TdsInfo {
        const {
            tdsExempted,
            tdsLowerDeduction,
        
            // common / exempted
            tdsSection,
            tdsExemptionNo,
            tdsValidityDate,
        
            // not exempted
            tdsNo,
            panNumber,
            taxRate,
        
            // lower deduction
            lowerDeductionCertNo,
            lowerDeductionValidity,
            lowerDeductionPercent,
            lowerDeductionLimit,
        } = formGroup.value;
        
        const tdsInfo: TdsInfo = {
            tdsExempted,
            tdsLowerDeduction,
            tdsSection: tdsSection ?? null,
        };
        
        if (!tdsExempted) {
            Object.assign(tdsInfo, {
            tdsNo: tdsNo ?? null,
            taxRate: taxRate ?? null,
            });
        }
        
        if (tdsExempted) {
            Object.assign(tdsInfo, {
            tdsExemptionNo: tdsExemptionNo ?? null,
            tdsValidityDate: tdsValidityDate ?? null,
            });
        }
        
        if (tdsLowerDeduction) {
            Object.assign(tdsInfo, {
            lowerDeductionCertNo: lowerDeductionCertNo ?? null,
            lowerDeductionValidity: lowerDeductionValidity ?? null,
            lowerDeductionPercent: lowerDeductionPercent ?? null,
            lowerDeductionLimit: lowerDeductionLimit ?? null,
            });
        }
        
        return tdsInfo;
    }
      
}